// Micro Kart Racers Multi-Track Tournament Server Engine (2-8 Players)
export class KartEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.currentTrackIndex = 0;
    this.totalLaps = 3;
    this.karts = {};
    this.eliminatedIds = new Set();
    this.loopInterval = null;
    this.qualifiers = [];

    this.tracks = [
      {
        id: 0,
        name: '🏁 STAGE 1: NEON SPEEDWAY',
        width: 900,
        height: 600,
        cx: 450,
        cy: 300,
        rx: 340,
        ry: 200,
        trackWidth: 120,
        startLine: { x: 450, y1: 40, y2: 160 },
        boostPads: [
          { x: 450, y: 100, r: 24 },
          { x: 790, y: 300, r: 24 },
          { x: 450, y: 500, r: 24 },
          { x: 110, y: 300, r: 24 }
        ]
      },
      {
        id: 1,
        name: '⚡ STAGE 2: CYBER CHICANES',
        width: 900,
        height: 600,
        cx: 450,
        cy: 300,
        rx: 320,
        ry: 180,
        trackWidth: 110,
        startLine: { x: 450, y1: 50, y2: 160 },
        boostPads: [
          { x: 300, y: 105, r: 22 },
          { x: 600, y: 105, r: 22 },
          { x: 770, y: 300, r: 22 },
          { x: 450, y: 480, r: 22 },
          { x: 130, y: 300, r: 22 }
        ]
      },
      {
        id: 2,
        name: '👑 FINAL STAGE: NEO METROPOLIS',
        width: 900,
        height: 600,
        cx: 450,
        cy: 300,
        rx: 360,
        ry: 220,
        trackWidth: 100,
        startLine: { x: 450, y1: 30, y2: 130 },
        boostPads: [
          { x: 450, y: 80, r: 24 },
          { x: 810, y: 300, r: 24 },
          { x: 450, y: 520, r: 24 },
          { x: 90, y: 300, r: 24 }
        ]
      }
    ];
  }

  start() {
    this.currentTrackIndex = 0;
    this.eliminatedIds.clear();
    this.startTrackRace();
  }

  startTrackRace() {
    this.karts = {};
    this.qualifiers = [];
    const track = this.tracks[this.currentTrackIndex];
    const colors = ['#ef4444', '#38bdf8', '#22c55e', '#facc15', '#a855f7', '#ec4899', '#f97316', '#06b6d4'];

    const activePlayers = this.room.players.filter(p => !this.eliminatedIds.has(p.id));

    // If solo player testing, add AI bot racers!
    if (activePlayers.length === 1) {
      const bots = [
        { id: 'bot_turbo', name: 'Turbo Bot 🏎️', isBot: true },
        { id: 'bot_speedy', name: 'Speedy Bot ⚡', isBot: true }
      ];
      activePlayers.push(...bots);
    }

    activePlayers.forEach((p, idx) => {
      this.karts[p.id] = {
        id: p.id,
        name: p.name,
        color: colors[idx % colors.length],
        x: 410 + (idx % 4) * 26,
        y: 80 + Math.floor(idx / 4) * 30,
        angle: 0,
        speed: 0,
        lap: 1,
        checkpoint: 0,
        input: { forward: false, backward: false, left: false, right: false },
        isFinished: false,
        isBot: p.isBot || false,
        isEliminated: false
      };
    });

    this.io.to(this.room.code).emit('kart_trackStarted', {
      track,
      stageNumber: this.currentTrackIndex + 1,
      totalStages: this.tracks.length,
      karts: this.karts
    });

    if (this.loopInterval) clearInterval(this.loopInterval);
    this.loopInterval = setInterval(() => this.update(), 1000 / 30);
  }

  handleInput(playerId, input) {
    const k = this.karts[playerId];
    if (k && !k.isFinished && !k.isEliminated) {
      k.input = input;
    }
  }

  update() {
    const track = this.tracks[this.currentTrackIndex];
    let allFinished = true;

    for (const id in this.karts) {
      const k = this.karts[id];
      if (k.isEliminated) continue;

      if (k.isFinished) continue;
      allFinished = false;

      // Bot AI Steering around track
      if (k.isBot) {
        k.input.forward = true;
        const targetAngle = Math.atan2(k.y - track.cy, k.x - track.cx) + Math.PI / 2;
        let diff = targetAngle - k.angle;
        while (diff < -Math.PI) diff += Math.PI * 2;
        while (diff > Math.PI) diff -= Math.PI * 2;
        k.angle += Math.sign(diff) * Math.min(0.08, Math.abs(diff));
      }

      // Acceleration & Steering
      if (k.input.forward) k.speed = Math.min(8.0, k.speed + 0.3);
      else if (k.input.backward) k.speed = Math.max(-3.0, k.speed - 0.25);
      else k.speed *= 0.95;

      if (k.input.left && Math.abs(k.speed) > 0.3) k.angle -= 0.08;
      if (k.input.right && Math.abs(k.speed) > 0.3) k.angle += 0.08;

      // Position update
      let nextX = k.x + Math.cos(k.angle) * k.speed;
      let nextY = k.y + Math.sin(k.angle) * k.speed;

      // STRICT SCREEN BOUNDARIES (Never allow leaving canvas)
      nextX = Math.max(20, Math.min(track.width - 20, nextX));
      nextY = Math.max(20, Math.min(track.height - 20, nextY));

      // Grass Off-Track Check (Distance to oval centerline)
      const normX = (nextX - track.cx) / track.rx;
      const normY = (nextY - track.cy) / track.ry;
      const distFromCenter = Math.sqrt(normX * normX + normY * normY);

      // On track if distFromCenter is between 0.75 and 1.25
      const isOnTrack = distFromCenter >= 0.7 && distFromCenter <= 1.3;
      if (!isOnTrack) {
        k.speed = Math.min(1.8, k.speed * 0.85); // Heavy grass slowdown
      }

      k.x = nextX;
      k.y = nextY;

      // Boost pad check
      for (const bp of track.boostPads) {
        if (Math.hypot(k.x - bp.x, k.y - bp.y) < bp.r + 14) {
          k.speed = Math.min(13.0, k.speed + 2.5);
        }
      }

      // Checkpoint tracking around track
      if (k.checkpoint === 0 && k.x > track.cx + track.rx * 0.7) k.checkpoint = 1;
      else if (k.checkpoint === 1 && k.y > track.cy + track.ry * 0.7) k.checkpoint = 2;
      else if (k.checkpoint === 2 && k.x < track.cx - track.rx * 0.7) k.checkpoint = 3;
      else if (k.checkpoint === 3 && k.y < track.cy - track.ry * 0.5 && k.x >= track.cx - 20 && k.x <= track.cx + 40) {
        k.checkpoint = 0;
        k.lap++;

        if (k.lap > this.totalLaps) {
          k.isFinished = true;
          this.qualifiers.push(k);

          this.io.to(this.room.code).emit('kart_lapCompleted', {
            id: k.id,
            name: k.name,
            finishRank: this.qualifiers.length
          });
        }
      }
    }

    this.io.to(this.room.code).emit('kart_state', {
      karts: this.karts,
      track
    });

    const activeCount = Object.values(this.karts).filter(k => !k.isEliminated).length;
    if (this.qualifiers.length >= activeCount - 1 && activeCount > 1) {
      this.handleTrackFinish();
    } else if (activeCount === 1 && this.qualifiers.length === 1) {
      this.handleTrackFinish();
    }
  }

  handleTrackFinish() {
    clearInterval(this.loopInterval);

    // Identify last place kart
    const activeList = Object.values(this.karts).filter(k => !k.isEliminated);
    const lastPlace = activeList.find(k => !this.qualifiers.includes(k)) || this.qualifiers[this.qualifiers.length - 1];

    if (this.currentTrackIndex < this.tracks.length - 1 && activeList.length > 2) {
      // Eliminate last place and advance
      if (lastPlace) {
        this.eliminatedIds.add(lastPlace.id);
        lastPlace.isEliminated = true;
      }

      this.io.to(this.room.code).emit('kart_stageElimination', {
        eliminatedName: lastPlace?.name || 'Driver',
        qualifiers: this.qualifiers.map(q => q.name),
        nextStage: this.currentTrackIndex + 2
      });

      this.currentTrackIndex++;
      setTimeout(() => this.startTrackRace(), 4000);
    } else {
      // Grand Final Champion
      const grandChampion = this.qualifiers[0] || activeList[0];
      this.io.to(this.room.code).emit('kart_gameOver', { winner: grandChampion });
    }
  }

  destroy() {
    if (this.loopInterval) clearInterval(this.loopInterval);
  }
}
